import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useBranding } from "./Layout";
import { Check, Download, FileText, PenLine } from "lucide-react";
import SignForm from "./SignForm";

/* The forms a seller's agent has asked them for, from the agent's own library.

   A form the agent has placed fields on can be signed here. One with no
   fields on it yet is still just a document: the client downloads the blank,
   fills it in however they fill things in, and gets it back to the agent. The
   card says plainly which kind it is rather than offering a button that
   wouldn't do anything.

   Renders nothing at all when the agent has no library -- an empty section
   headed "Forms from your agent" would be a worse answer than silence. */

export default function MyForms() {
  const branding = useBranding();
  const [forms, setForms] = useState([]);
  const [signed, setSigned] = useState({});
  const [busy, setBusy] = useState("");
  const [signing, setSigning] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/form-library");
      setForms(data.forms || []);
    } catch {
      // Silent: this is an extra section, not the point of the page.
    }
    try {
      const { data } = await api.get("/me/signed-forms");
      const by = {};
      (data.signed || []).forEach((s) => {
        // Newest first from the server, so the first one wins.
        if (!by[s.template_id]) by[s.template_id] = s;
      });
      setSigned(by);
    } catch {
      /* same */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (forms.length === 0) return null;

  const agent = branding?.agent_first_name || "your agent";

  const download = async (t) => {
    setBusy(t.template_id);
    try {
      const { data } = await api.get(`/form-library/${t.template_id}/file`,
                                     { responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = t.filename || `${t.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 10000);
    } finally { setBusy(""); }
  };

  const getSigned = async (s) => {
    setBusy(s.signed_form_id);
    try {
      const { data } = await api.get(`/signed-form/${s.signed_form_id}/file`,
                                     { responseType: "blob" });
      const href = URL.createObjectURL(data);
      window.open(href, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } finally { setBusy(""); }
  };

  const anySignable = forms.some((t) => (t.field_count || 0) > 0);

  return (
    <div style={{ marginTop: 56 }} data-testid="my-forms">
      <span className="overline">From {agent}</span>
      <h2 className="font-serif" style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 500,
                                          margin: "8px 0 10px", letterSpacing: "-0.01em" }}>
        Forms to sign
      </h2>
      <p style={{ color: "var(--text-muted)", maxWidth: 680, marginTop: 0 }}>
        {anySignable
          ? `Sign these here and ${agent} gets them straight away. Anything without a sign button is one to download, fill in, and send back.`
          : `Download these, fill them in, and get them back to ${agent}.`}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: 20, marginTop: 28 }}>
        {forms.map((t) => {
          const done = signed[t.template_id];
          const signable = (t.field_count || 0) > 0;
          return (
            <div key={t.template_id} className="card" style={{ padding: 28, background: "#fff" }}
                 data-testid={`my-form-${t.template_id}`}>
              {done
                ? <Check size={22} style={{ color: "var(--primary)" }} />
                : <FileText size={22} style={{ color: "var(--primary)" }} />}
              <h3 className="font-serif" style={{ fontSize: 22, marginTop: 16, marginBottom: 0,
                                                  fontWeight: 500, letterSpacing: "-0.01em" }}>
                {t.name}
              </h3>
              {t.note && (
                <p style={{ color: "var(--text-muted)", marginTop: 6, fontSize: 14 }}>{t.note}</p>
              )}
              <p style={{ color: "var(--text-muted)", marginTop: 6, fontSize: 13 }}>
                {done ? (
                  <>Signed {new Date(done.signed_at).toLocaleDateString()}.</>
                ) : (
                  <>
                    {t.completed_by === "both"
                      ? `You and ${agent} each fill in part of this one.`
                      : "Yours to fill in."}
                    {t.page_count ? ` ${t.page_count} page${t.page_count === 1 ? "" : "s"}.` : ""}
                  </>
                )}
              </p>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
                {done ? (
                  <button type="button" className="btn-secondary" disabled={busy === done.signed_form_id}
                          onClick={() => getSigned(done)} data-testid={`my-form-signed-${t.template_id}`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <FileText size={14} /> {busy === done.signed_form_id ? "Opening…" : "See signed copy"}
                  </button>
                ) : signable ? (
                  <button type="button" className="btn-primary" onClick={() => setSigning(t.template_id)}
                          data-testid={`my-form-sign-${t.template_id}`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <PenLine size={14} /> Review and sign
                  </button>
                ) : null}
                <button type="button" className="btn-ghost" disabled={busy === t.template_id}
                        onClick={() => download(t)} data-testid={`my-form-dl-${t.template_id}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                  <Download size={14} /> {busy === t.template_id ? "Getting it…" : "Blank copy"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {signing && (
        <SignForm templateId={signing} onClose={() => setSigning(null)}
                  onSigned={() => { setSigning(null); load(); }} />
      )}
    </div>
  );
}
